import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Stack,
  Autocomplete,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { PotentialSupplier, PipelineStage } from '../types';
import { colors } from '../theme';
import { dataService } from '../services/dataService';

// ============================================================================
// WARNING: PERSISTENCIA CON localStorage - PARA DEMO SOLAMENTE
// ============================================================================
// Este módulo usa localStorage para persistir proveedores potenciales entre recargas.
// ESTO NO ES ADECUADO PARA PRODUCCIÓN por:
//   - Limitado a 5-10MB por dominio
//   - No sincroniza entre usuarios/dispositivos
//   - No hay validación server-side
//   - No hay backup automático
//   - Datos se pierden si el usuario limpia el navegador
//
// EN PRODUCCIÓN, se necesita implementar:
//   1. POST   /api/potential-suppliers          - Crear nuevo lead
//   2. GET    /api/potential-suppliers          - Listar todos los leads
//   3. GET    /api/potential-suppliers/:id      - Detalle de un lead
//   4. PUT    /api/potential-suppliers/:id      - Actualizar lead
//   5. PATCH  /api/potential-suppliers/:id/stage - Cambiar etapa (drag & drop)
//   6. DELETE /api/potential-suppliers/:id      - Eliminar lead
//   7. POST   /api/potential-suppliers/:id/convert - Convertir a proveedor activo
//
// Consideraciones adicionales:
//   - Timestamps deben generarse en el servidor (no confiar en timestamp del cliente)
//   - Validar que las categorías asignadas existan en el Supply Master
//   - Implementar log de cambios de etapa (pipeline history)
//   - Considerar permisos de usuario (quién puede crear/editar/eliminar leads)
//   - Notificaciones cuando un lead cambia de etapa
//   - Integración con NetSuite al aprobar un lead
// ============================================================================

const STORAGE_KEYS = {
  POTENTIAL_SUPPLIERS: 'interbarge_potential_suppliers',
};

// Configuración de colores por etapa
const stageColors: Record<PipelineStage, string> = {
  lead: '#9e9e9e',
  qualified: '#2196f3',
  proposal: '#ff9800',
  negotiation: '#ffc107',
  approved: '#4caf50',
  rejected: '#f44336',
};

const stageLabels: Record<PipelineStage, string> = {
  lead: 'Lead',
  qualified: 'Calificado',
  proposal: 'Propuesta',
  negotiation: 'Negociación',
  approved: 'Aprobado',
  rejected: 'Rechazado',
};

// Mock data de ejemplo (usado solo en primera carga si localStorage está vacío)
const initialSuppliers: PotentialSupplier[] = [
  {
    id: '1',
    name: 'Marine Services Corp',
    stage: 'lead',
    contactName: 'John Smith',
    contactEmail: 'john@marineservices.com',
    contactPhone: '+1 555-0101',
    categories: ['Engine Parts', 'Maintenance'],
    notes: 'Recommended by Houston office',
    createdAt: new Date('2026-03-01'),
    updatedAt: new Date('2026-03-01'),
    stageChangedAt: new Date('2026-03-01'),
    expectedCloseDate: new Date('2026-04-15'),
  },
  {
    id: '2',
    name: 'Gulf Coast Suppliers',
    stage: 'qualified',
    contactName: 'Maria Garcia',
    contactEmail: 'maria@gulfcoast.com',
    contactPhone: '+1 555-0102',
    categories: ['Safety Equipment', 'Uniforms'],
    notes: 'Strong safety record',
    createdAt: new Date('2026-02-15'),
    updatedAt: new Date('2026-03-05'),
    stageChangedAt: new Date('2026-03-05'),
    expectedCloseDate: new Date('2026-03-30'),
  },
  {
    id: '3',
    name: 'Atlantic Marine Supply',
    stage: 'proposal',
    contactName: 'Robert Johnson',
    contactEmail: 'robert@atlanticmarine.com',
    contactPhone: '+1 555-0103',
    categories: ['Deck Equipment', 'Rigging'],
    notes: 'Proposal sent 2026-03-08',
    createdAt: new Date('2026-02-01'),
    updatedAt: new Date('2026-03-08'),
    stageChangedAt: new Date('2026-03-08'),
    expectedCloseDate: new Date('2026-03-25'),
  },
  {
    id: '4',
    name: 'Pacific Trading Ltd',
    stage: 'negotiation',
    contactName: 'Lisa Chen',
    contactEmail: 'lisa@pacifictrading.com',
    contactPhone: '+1 555-0104',
    categories: ['Electronics', 'Navigation'],
    notes: 'Price negotiation in progress',
    createdAt: new Date('2026-01-20'),
    updatedAt: new Date('2026-03-10'),
    stageChangedAt: new Date('2026-03-10'),
    expectedCloseDate: new Date('2026-03-20'),
  },
  {
    id: '5',
    name: 'Coastal Equipment Co',
    stage: 'approved',
    contactName: 'David Brown',
    contactEmail: 'david@coastalequip.com',
    contactPhone: '+1 555-0105',
    categories: ['Fuel Systems', 'Pumps'],
    notes: 'Approved - pending onboarding',
    createdAt: new Date('2026-01-10'),
    updatedAt: new Date('2026-03-11'),
    stageChangedAt: new Date('2026-03-11'),
  },
  {
    id: '6',
    name: 'Northern Logistics Inc',
    stage: 'lead',
    contactName: 'Sarah Williams',
    contactEmail: 'sarah@northernlogistics.com',
    contactPhone: '+1 555-0106',
    categories: ['Transportation', 'Warehousing'],
    notes: 'Initial contact made',
    createdAt: new Date('2026-03-09'),
    updatedAt: new Date('2026-03-09'),
    stageChangedAt: new Date('2026-03-09'),
    expectedCloseDate: new Date('2026-04-20'),
  },
];

// ============================================================================
// HELPERS PARA PERSISTENCIA EN localStorage (TEMPORAL - VER WARNING ARRIBA)
// ============================================================================

/**
 * Carga proveedores potenciales desde localStorage
 * WARNING: En producción, usar GET /api/potential-suppliers
 */
const loadPotentialSuppliersFromStorage = (): PotentialSupplier[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.POTENTIAL_SUPPLIERS);
    if (!stored) {
      console.log('[PotentialSuppliers] No hay datos en localStorage, usando mock inicial');
      return initialSuppliers;
    }

    const parsed = JSON.parse(stored);
    
    // Convertir strings ISO a Date objects
    return parsed.map((s: any) => ({
      ...s,
      createdAt: new Date(s.createdAt),
      updatedAt: new Date(s.updatedAt),
      stageChangedAt: new Date(s.stageChangedAt),
      expectedCloseDate: s.expectedCloseDate ? new Date(s.expectedCloseDate) : undefined,
    }));
  } catch (error) {
    console.error('[PotentialSuppliers] Error cargando desde localStorage:', error);
    return initialSuppliers;
  }
};

/**
 * Guarda proveedores potenciales en localStorage
 * WARNING: En producción, usar POST/PUT /api/potential-suppliers/:id
 */
const savePotentialSuppliersToStorage = (suppliers: PotentialSupplier[]) => {
  try {
    localStorage.setItem(STORAGE_KEYS.POTENTIAL_SUPPLIERS, JSON.stringify(suppliers));
    console.log('[PotentialSuppliers] Guardado en localStorage:', suppliers.length, 'leads');
  } catch (error) {
    console.error('[PotentialSuppliers] Error guardando en localStorage:', error);
    // TODO: En producción, mostrar notificación al usuario que los cambios no se guardaron
  }
};

// Componente de tarjeta draggable
interface SupplierCardProps {
  supplier: PotentialSupplier;
  index: number;
  onEdit: (supplier: PotentialSupplier) => void;
  onDelete: (id: string) => void;
}

const SupplierCard: React.FC<SupplierCardProps> = ({ supplier, index, onEdit, onDelete }) => {
  return (
    <Draggable draggableId={supplier.id} index={index}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          sx={{
            mb: 1.5,
            borderLeft: 4,
            borderColor: stageColors[supplier.stage],
            backgroundColor: snapshot.isDragging ? 'action.hover' : 'background.paper',
            boxShadow: snapshot.isDragging ? 6 : 1,
          }}
        >
          <CardContent sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
              <Box {...provided.dragHandleProps} sx={{ mr: 1, mt: 0.5, cursor: 'grab' }}>
                <DragIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </Box>
              <Typography variant="h6" sx={{ fontSize: '0.95rem', fontWeight: 600, flex: 1 }}>
                {supplier.name}
              </Typography>
            </Box>

            {supplier.contactName && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', mb: 0.5, ml: 3 }}>
                {supplier.contactName}
              </Typography>
            )}

            {supplier.contactEmail && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, ml: 3 }}>
                <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {supplier.contactEmail}
                </Typography>
              </Box>
            )}

            {supplier.contactPhone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, ml: 3 }}>
                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  {supplier.contactPhone}
                </Typography>
              </Box>
            )}

            {supplier.categories && supplier.categories.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, ml: 3 }}>
                {supplier.categories.slice(0, 2).map((cat, idx) => (
                  <Chip
                    key={idx}
                    label={cat}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                ))}
                {supplier.categories.length > 2 && (
                  <Chip
                    label={`+${supplier.categories.length - 2}`}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
              </Box>
            )}

            {supplier.notes && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontStyle: 'italic', mt: 1, ml: 3 }}
              >
                {supplier.notes.length > 60 ? supplier.notes.substring(0, 60) + '...' : supplier.notes}
              </Typography>
            )}
          </CardContent>

          <CardActions sx={{ justifyContent: 'flex-end', pt: 0, pb: 1 }}>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                onEdit(supplier);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                onDelete(supplier.id);
              }} 
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </CardActions>
        </Card>
      )}
    </Draggable>
  );
};

// Componente de columna
interface KanbanColumnProps {
  stage: PipelineStage;
  suppliers: PotentialSupplier[];
  onEdit: (supplier: PotentialSupplier) => void;
  onDelete: (id: string) => void;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({ stage, suppliers, onEdit, onDelete }) => {
  return (
    <Paper
      sx={{
        p: 2,
        minHeight: 500,
        bgcolor: 'grey.50',
        borderTop: 3,
        borderColor: stageColors[stage],
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
          {stageLabels[stage]}
        </Typography>
        <Chip
          label={suppliers.length}
          size="small"
          sx={{
            bgcolor: stageColors[stage],
            color: 'white',
            fontWeight: 600,
          }}
        />
      </Box>

      <Droppable droppableId={stage}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              minHeight: 400,
              backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
              borderRadius: 1,
              transition: 'background-color 0.2s',
            }}
          >
            {suppliers.map((supplier, index) => (
              <SupplierCard
                key={supplier.id}
                supplier={supplier}
                index={index}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </Paper>
  );
};

const PotentialSuppliers: React.FC = () => {
  const [suppliers, setSuppliers] = useState<PotentialSupplier[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<PotentialSupplier | null>(null);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    stage: 'lead' as PipelineStage,
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    categories: [] as string[],
    notes: '',
    expectedCloseDate: '',
  });

  // Cargar proveedores desde localStorage al iniciar
  useEffect(() => {
    const loaded = loadPotentialSuppliersFromStorage();
    setSuppliers(loaded);
  }, []);

  // Auto-guardar cuando cambian los proveedores
  useEffect(() => {
    if (suppliers.length > 0) {
      savePotentialSuppliersToStorage(suppliers);
    }
  }, [suppliers]);

  // Cargar categorías disponibles del Supply Master
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        await dataService.loadPurchaseOrders('/00_Informacion utilizada/PurchaseOrders.csv');
        const categories = dataService.getCategories();
        setAvailableCategories(categories.sort());
        console.log('[PotentialSuppliers] Categorías cargadas desde Supply Master:', categories.length);
      } catch (error) {
        console.error('[PotentialSuppliers] Error cargando categorías:', error);
        // Si falla, usar array vacío (el usuario podrá escribir a mano)
        setAvailableCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;

    // Dropped outside
    if (!destination) {
      return;
    }

    // No cambió de posición
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const newStage = destination.droppableId as PipelineStage;
    const now = new Date();

    // WARNING: En producción, este cambio de etapa debe enviarse al servidor
    // TODO: Implementar PATCH /api/potential-suppliers/:id/stage
    // Body: { stage: newStage, stageChangedAt: serverTimestamp }
    // Considerar: log de cambios de etapa, notificaciones, validaciones de negocio

    setSuppliers((prev) => {
      const updated = prev.map((s) =>
        s.id === draggableId
          ? { 
              ...s, 
              stage: newStage, 
              stageChangedAt: now, 
              updatedAt: now 
            }
          : s
      );

      // Reordenar dentro de la columna
      const sourceSuppliers = updated.filter((s) => s.stage === source.droppableId);
      const movedSupplier = updated.find((s) => s.id === draggableId)!;

      if (source.droppableId === destination.droppableId) {
        // Mismo stage, solo reordenar
        sourceSuppliers.splice(source.index, 1);
        sourceSuppliers.splice(destination.index, 0, movedSupplier);
        return [
          ...updated.filter((s) => s.stage !== source.droppableId),
          ...sourceSuppliers,
        ];
      } else {
        // Diferente stage
        return updated;
      }
    });
  };

  const handleOpenDialog = (supplier?: PotentialSupplier) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setFormData({
        name: supplier.name,
        stage: supplier.stage,
        contactName: supplier.contactName || '',
        contactEmail: supplier.contactEmail || '',
        contactPhone: supplier.contactPhone || '',
        categories: supplier.categories || [],
        notes: supplier.notes || '',
        expectedCloseDate: supplier.expectedCloseDate?.toISOString().split('T')[0] || '',
      });
    } else {
      setEditingSupplier(null);
      setFormData({
        name: '',
        stage: 'lead',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        categories: [],
        notes: '',
        expectedCloseDate: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSupplier(null);
  };

  const handleSaveSupplier = () => {
    const now = new Date();

    // WARNING: En producción, usar timestamps del servidor, no del cliente
    // TODO: POST /api/potential-suppliers (crear) o PUT /api/potential-suppliers/:id (editar)

    if (editingSupplier) {
      // Editar existente
      setSuppliers((prev) =>
        prev.map((s) =>
          s.id === editingSupplier.id
            ? {
                ...s,
                name: formData.name,
                stage: formData.stage,
                contactName: formData.contactName,
                contactEmail: formData.contactEmail,
                contactPhone: formData.contactPhone,
                categories: formData.categories,
                notes: formData.notes,
                expectedCloseDate: formData.expectedCloseDate ? new Date(formData.expectedCloseDate) : undefined,
                updatedAt: now,
                stageChangedAt: s.stage !== formData.stage ? now : s.stageChangedAt,
              }
            : s
        )
      );
    } else {
      // Crear nuevo
      const newSupplier: PotentialSupplier = {
        id: `new-${Date.now()}`,
        name: formData.name,
        stage: formData.stage,
        contactName: formData.contactName,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        categories: formData.categories,
        notes: formData.notes,
        createdAt: now,
        updatedAt: now,
        stageChangedAt: now,
        expectedCloseDate: formData.expectedCloseDate ? new Date(formData.expectedCloseDate) : undefined,
      };
      setSuppliers((prev) => [...prev, newSupplier]);
    }

    handleCloseDialog();
  };

  const handleDeleteSupplier = (id: string) => {
    if (window.confirm('¿Está seguro que desea eliminar este proveedor potencial?')) {
      // WARNING: En producción, usar DELETE /api/potential-suppliers/:id
      // Considerar: soft delete, log de auditoría, permisos de usuario
      setSuppliers((prev) => prev.filter((s) => s.id !== id));
    }
  };

  const stages: PipelineStage[] = ['lead', 'qualified', 'proposal', 'negotiation', 'approved', 'rejected'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Contactos comerciales
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {suppliers.length} prospectos en pipeline
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: colors.brand.red,
            '&:hover': { bgcolor: colors.primary.dark },
          }}
        >
          Nuevo Lead
        </Button>
      </Box>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Grid container spacing={2}>
          {stages.map((stage) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={stage}>
              <KanbanColumn
                stage={stage}
                suppliers={suppliers.filter((s) => s.stage === stage)}
                onEdit={handleOpenDialog}
                onDelete={handleDeleteSupplier}
              />
            </Grid>
          ))}
        </Grid>
      </DragDropContext>

      {/* Dialog para crear/editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingSupplier ? 'Editar Proveedor' : 'Nuevo Lead'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Las categorías están sincronizadas con el Supply Master ({availableCategories.length} categorías disponibles).
            También puede escribir categorías nuevas si es necesario.
          </Alert>
          
          <Stack spacing={2}>
            <TextField
              label="Nombre de la Empresa"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Etapa del Pipeline"
              value={formData.stage}
              onChange={(e) => setFormData({ ...formData, stage: e.target.value as PipelineStage })}
              select
              fullWidth
            >
              {stages.map((stage) => (
                <MenuItem key={stage} value={stage}>
                  {stageLabels[stage]}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Nombre de Contacto"
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              fullWidth
            />

            <TextField
              label="Email"
              value={formData.contactEmail}
              onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
              type="email"
              fullWidth
            />

            <TextField
              label="Teléfono"
              value={formData.contactPhone}
              onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
              fullWidth
            />

            <Autocomplete
              multiple
              options={availableCategories}
              value={formData.categories}
              onChange={(_, newValue) => setFormData({ ...formData, categories: newValue })}
              loading={loadingCategories}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categorías del Supply Master"
                  placeholder={formData.categories.length === 0 ? "Seleccione o escriba categorías" : ""}
                  helperText={
                    loadingCategories 
                      ? "Cargando categorías del Supply Master..." 
                      : `${availableCategories.length} categorías disponibles. Puede escribir categorías nuevas.`
                  }
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
            />

            <TextField
              label="Fecha Esperada de Cierre"
              value={formData.expectedCloseDate}
              onChange={(e) => setFormData({ ...formData, expectedCloseDate: e.target.value })}
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Notas"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSaveSupplier}
            variant="contained"
            disabled={!formData.name}
          >
            {editingSupplier ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PotentialSuppliers;
